"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { sanity } from "@/sanity/lib/client";
import { categoriesLinksQuery, categoryBySlug, type CategoryBySlugType, type CategoryLinksType } from "@/sanity/query/getCategories";
import type { ProjectType } from "@/sanity/query/project";
type GlobalContextValues = {
  categories: CategoryBySlugType[];
  selected: {
    category: CategoryBySlugType | null;
    project: ProjectType | null;
  };
  hovered: {
    category: CategoryBySlugType | null;
    project: ProjectType | null;
  };
  setSelected: React.Dispatch<React.SetStateAction<{
    category: CategoryBySlugType | null;
    project: ProjectType | null;
  }>>;
  setHovered: React.Dispatch<React.SetStateAction<{
    category: CategoryBySlugType | null;
    project: ProjectType | null;
  }>>;
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalIndex: number;
  setModalIndex: React.Dispatch<React.SetStateAction<number>>;
  assets: (NonNullable<ProjectType["thumbnail"]> | ProjectType["content"][number])[];
};
async function fetchData() {
  // todo: write a single query to do this.
  const categories = await sanity.fetch<CategoryLinksType[]>(categoriesLinksQuery);
  return Promise.all(categories.map(category => sanity.fetch<CategoryBySlugType>(categoryBySlug, {
    slug: category.slug
  })));
}
export const GlobalContext = createContext<GlobalContextValues | null>(null);
export const useGlobalContext = () => {
  const $ = _c(1);
  const ctx = useContext(GlobalContext);
  if (!ctx) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {};
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0 as GlobalContextValues;
  }
  return ctx;
};
export function GlobalStateProvider(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [categories, setCategories] = useState(t1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      category: null,
      project: null
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [selected, setSelected] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      category: null,
      project: null
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [hovered, setHovered] = useState(t3);
  let t4;
  if ($[3] !== selected.project) {
    t4 = selected.project ? [selected.project.thumbnail, ...(selected.project.content ?? [])].filter(_temp) : [];
    $[3] = selected.project;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const assets = t4;
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      fetchData().then(setCategories);
    };
    t6 = [];
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] !== assets || $[8] !== categories || $[9] !== hovered || $[10] !== modalIndex || $[11] !== modalOpen || $[12] !== selected) {
    t7 = {
      categories,
      modalOpen,
      setModalOpen,
      modalIndex,
      setModalIndex,
      assets,
      selected,
      setSelected,
      hovered,
      setHovered
    };
    $[7] = assets;
    $[8] = categories;
    $[9] = hovered;
    $[10] = modalIndex;
    $[11] = modalOpen;
    $[12] = selected;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const value = t7;
  let t8;
  if ($[14] !== children || $[15] !== value) {
    t8 = <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
    $[14] = children;
    $[15] = value;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function _temp(image) {
  return image != null;
}